/**
 * Búsqueda del reporte de facturas elegibles.
 * Una sola definición de "la fila coincide con el término", usada por las columnas de la grilla
 * y por los ids que reciben las acciones masivas. Sin dependencias de MUI ni de React.
 */
import type { EligibleInvoiceItemDto } from "@/types/jobs";

export type SearchableInvoiceRow = Pick<
  EligibleInvoiceItemDto,
  "CarrierInvoiceId" | "InvoiceNumber" | "BookingId" | "BookingReference"
>;

const includesText = (text: string, term: string) => text.toLowerCase().includes(term.toLowerCase());

export function getBookingDisplayText(row: Pick<SearchableInvoiceRow, "BookingId" | "BookingReference">): string {
  return row.BookingReference ?? `#${row.BookingId}`;
}

export const matchesInvoiceNumber = (row: SearchableInvoiceRow, term: string) =>
  includesText(row.InvoiceNumber, term);

export const matchesBooking = (row: SearchableInvoiceRow, term: string) =>
  includesText(getBookingDisplayText(row), term);

/** Única tokenización del buscador: separa por cualquier espacio Unicode y descarta vacíos. */
export function tokenizeSearchInput(text: string): string[] {
  return text.split(/\s+/u).filter((t) => t !== "");
}

/** Vista de los valores del quick filter de la grilla; coincide con `tokenizeSearchInput` por construcción. */
export function getSearchTerms(values: readonly unknown[] | undefined): string[] {
  return (values ?? []).flatMap((v) => tokenizeSearchInput(String(v)));
}

export function getFilteredInvoiceIds(rows: readonly SearchableInvoiceRow[], terms: string[]): number[] {
  return rows
    .filter((r) => terms.every((t) => matchesInvoiceNumber(r, t) || matchesBooking(r, t)))
    .map((r) => r.CarrierInvoiceId);
}

export function resolveSelectedIds(
  model: { type: "include" | "exclude"; ids: ReadonlySet<string | number> },
  allIds: number[],
): number[] {
  const ids = new Set(Array.from(model.ids, Number));
  return model.type === "include" ? Array.from(ids) : allIds.filter((id) => !ids.has(id));
}

export interface BulkAllAction {
  ids: number[];
  label: string;
  enabled: boolean;
  disabledReason?: string;
}

export function resolveBulkAllAction(input: {
  allIds: number[];
  filteredIds: number[];
  searchActive: boolean;
  hasColumnFilter: boolean;
}): BulkAllAction {
  const { allIds, filteredIds, searchActive, hasColumnFilter } = input;
  const ids = searchActive ? filteredIds : allIds;
  const label = searchActive ? `Generar filtradas (${ids.length})` : `Generar todas (${ids.length})`;
  if (hasColumnFilter) {
    return {
      ids: [],
      label,
      enabled: false,
      disabledReason:
        "Hay un filtro de columna activo: selecciona las filas y usa «Generar sel.» " +
        "(si marcaste todas antes de filtrar, incluye también las que el filtro de columna oculta)",
    };
  }
  return { ids, label, enabled: ids.length > 0 };
}
